import { Camera, MessageSquare, PenLine } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  TASK_JOB_TYPE,
  TASK_PROOF_TYPE,
  type TaskJob,
  type TaskJobType,
  type TaskProof,
  type TaskProofType,
} from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A proof paired with the job step (pickup/delivery) it was captured on — proofs live on `TaskJob.proofs`. */
export interface JobProof {
  jobType: TaskJobType;
  proof: TaskProof;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Icon shown next to each proof type in `ProofsTable` and `ProofViewDialog`. */
export const PROOF_TYPE_ICONS: Record<TaskProofType, LucideIcon> = {
  [TASK_PROOF_TYPE.COMMENT]: MessageSquare,
  [TASK_PROOF_TYPE.IMAGES]: Camera,
  [TASK_PROOF_TYPE.SIGNATURE]: PenLine,
};

/**
 * i18n key for the pickup/delivery step label of a `JobProof.jobType`. Cross-namespace —
 * reuses the existing `orders` namespace's `drawer.pickup`/`drawer.delivery` keys (already
 * shared this way by `TaskPickupDeliveryTimeline`) rather than duplicating them in `tasks`.
 */
export const PROOF_STEP_LABEL_KEYS: Record<TaskJobType, string> = {
  [TASK_JOB_TYPE.DELIVERY]: 'orders:drawer.delivery',
  [TASK_JOB_TYPE.PICKUP]: 'orders:drawer.pickup',
};

/** i18n key for the human-readable proof type label (images displays as "Photo" per the design). */
export const PROOF_TYPE_LABEL_KEYS: Record<TaskProofType, string> = {
  [TASK_PROOF_TYPE.COMMENT]: 'proofs.type.comment',
  [TASK_PROOF_TYPE.IMAGES]: 'proofs.type.photo',
  [TASK_PROOF_TYPE.SIGNATURE]: 'proofs.type.signature',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Normalizes `createdAt` to a `Date` before reading its time.
 * Typed as `Date` on `TaskProof`, but the API client does not hydrate dates —
 * over HTTP it actually arrives as an ISO string, so a raw `.getTime()` call
 * would throw. Mirrors the `formatShortDateSafe` defensive pattern in
 * `pharos-lib-front-utils`.
 */
function toCreatedAtTime(createdAt: Date): number {
  const parsed = createdAt instanceof Date ? createdAt : new Date(createdAt);
  return parsed.getTime();
}

/** Flattens every job's `proofs` into `{ proof, jobType }` pairs, sorted by createdAt descending across all jobs. */
export function flattenJobProofs(jobs: TaskJob[]): JobProof[] {
  const pairs = jobs.flatMap((job) => (job.proofs ?? []).map((proof): JobProof => ({ jobType: job.type, proof })));
  return pairs.sort((a, b) => toCreatedAtTime(b.proof.createdAt) - toCreatedAtTime(a.proof.createdAt));
}

/**
 * Builds the shared proof-view dialog title: `{StepType} · {ProofType}`, with
 * an image count suffix (e.g. ` (2)`) appended for images proofs.
 */
export function buildProofViewTitle(pair: JobProof, t: (key: string) => string): string {
  const step = t(PROOF_STEP_LABEL_KEYS[pair.jobType]);
  const typeLabel = t(PROOF_TYPE_LABEL_KEYS[pair.proof.type]);
  const suffix = pair.proof.type === TASK_PROOF_TYPE.IMAGES ? ` (${pair.proof.images.length})` : '';
  return `${step} · ${typeLabel}${suffix}`;
}
